import _ from 'lodash'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'

import { baseApi, Tag } from './base'

import { Patient, UpdatePatientDto, CreatePatientDto, Parent, PatientIdentityStatus } from '@/core/models/patient.model'
import { SearchInsIdentityPatientsParams, SearchPatientsFilterParams, SearchRequestOptions } from '@/core/types/request'
import { SearchResponse } from '@/core/types/response'
import { PatientSourceEnum } from '../enums/patient-source.enum'
import { WithId } from '../types/shared'
import { Consultation } from '../models/consultation.model'
import { Document } from '../models/document.model'

dayjs.extend(timezone)

export type ListPatientsFilterQuery = {
  identityStatus?: PatientIdentityStatus[]
  insNIR?: string
  name?: string
  patientSource: PatientSourceEnum
  surname?: string
  secu?: number
}

type MergePatientsParams = {
  primaryPatient: string
  secondaryPatient: string
  overrides?: {
    userInfo?: Partial<Patient['userInfo']>
    contactInfo?: Partial<Patient['contactInfo']>
    email?: string
    notes?: string
    allergy?: string
    background?: string
    vaccins?: string
    treatment?: string
    height?: number
    weight?: number
    secu?: string
    pluginId?: string
    netsoinsId?: string
  }
  parentIds?: string[]
  mergeExpertises?: boolean
}

type GetPatientFilesParams = {
  patientId: string
  limit?: number
  skip?: number
}

export type { SearchPatientsFilterParams } from '@/core/types/request'

export const patientApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPatient: builder.query<Patient, string>({
      query: (patientId) => `/patients/${patientId}`,
    }),
    searchPatients: builder.query<SearchResponse<Patient>, SearchPatientsFilterParams>({
      query: (body) => ({
        url: '/patients/search',
        method: 'POST',
        body: { limit: 5, skip: 0, order: 'ASC', timezone: dayjs.tz.guess(), ...body },
      }),
      providesTags: (result) => (result ? result.items.map((item) => ({ type: Tag.PATIENT, id: item._id })) : []),
    }),
    searchInsIdentityPatients: builder.query<SearchResponse<Patient>, SearchInsIdentityPatientsParams>({
      query: (body) => ({
        url: '/patients/search/ins-identity',
        method: 'POST',
        body: {
          limit: 5,
          skip: 0,
          order: 'ASC',
          orderBy: 'userInfo.birthSurname',
          ...body,
        },
      }),
      providesTags: (result) => (result ? result.items.map((item) => ({ type: Tag.PATIENT, id: item._id })) : []),
    }),
    searchUnassignedPatients: builder.query<SearchResponse<Patient>, SearchPatientsFilterParams>({
      query: (body) => ({
        url: '/patients/search/unassigned',
        method: 'POST',
        body: { limit: 5, skip: 0, order: 'ASC', timezone: dayjs.tz.guess(), ...body },
      }),
    }),
    listPatients: builder.query<SearchResponse<Patient>, { query: ListPatientsFilterQuery; options?: SearchRequestOptions }>({
      query: ({ query, options }) => ({
        url: '/patients/query',
        method: 'POST',
        body: {
          query,
          options: _.merge(
            {
              limit: 5,
              order: 'ASC',
              skip: 0,
            },
            options
          ),
        },
      }),
    }),
    listPatientsWithIns: builder.query<Patient[], void>({
      query: () => ({ url: '/patients/ins', method: 'POST' }),
    }),
    lookupPatientByInsNIR: builder.query<{ patient: Patient | null }, string>({
      query: (insNIR) => ({ url: '/patients/ins-lookup', method: 'POST', body: { insNIR } }),
    }),
    createPatient: builder.mutation<Patient, CreatePatientDto>({
      query: (body) => ({ url: '/patients', method: 'POST', body }),
    }),
    updatePatient: builder.mutation<Patient, WithId<UpdatePatientDto>>({
      query: ({ id: patientId, ...body }) => ({ url: `/patients/${patientId}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id: patientId }) => [
        { type: Tag.INS_AUDIT_LOG, id: patientId },
        { type: Tag.INS_AUDIT_LOG, id: 'LIST' },
      ],
      async onQueryStarted({ id: patientId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedPatient } = await queryFulfilled
          dispatch(
            patientApi.util.updateQueryData('getPatient', patientId, (draftPatient) => {
              Object.assign(draftPatient, updatedPatient)
            })
          )
        } catch { }
      },
    }),
    getPatientConsultations: builder.query<SearchResponse<Consultation>, { patientId: string; options?: SearchRequestOptions }>({
      query: ({ patientId, options }) => ({
        url: `/patients/${patientId}/consultations`,
        method: 'POST',
        body: { ...options },
      }),
    }),
    getPatientFiles: builder.query<SearchResponse<Document>, GetPatientFilesParams>({
      query: ({ patientId, ...params }) => ({
        url: `/patients/${patientId}/files`,
        params,
      }),
    }),
    mergePatients: builder.mutation<Patient, MergePatientsParams>({
      query: ({ primaryPatient, secondaryPatient, overrides, parentIds, mergeExpertises }) => ({
        url: `/patients/merge`,
        method: 'POST',
        body: { primaryPatient, secondaryPatient, overrides, parentIds, mergeExpertises },
      }),
      invalidatesTags: (result) => (result ? [{ type: Tag.PATIENT, id: result._id }] : []),
    }),
    getOrCreateOrdoclicPatient: builder.query<Patient, string>({
      query: (patientId) => `/patients/${patientId}/ordoclic`,
    }),
    getOrCreateDesmosPatient: builder.query<Patient, string>({
      query: (patientId) => `/patients/${patientId}/desmos`,
    }),
    attachPatientToEstablishment: builder.mutation<void, { establishmentId: string; patientId: string }>({
      query: ({ establishmentId, patientId }) => ({
        url: `/patients/${patientId}/establishment/${establishmentId}`,
        method: 'POST',
      }),
    }),
  }),
  overrideExisting: false,
})
